import AppsIcon from "@mui/icons-material/Apps";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import { AllCategories, Category, MarketplaceCategories, PostCategories } from "config";
import React, { useRef, useState } from "react";

/**
 * Built once at module load rather than per render. All three sources are module constants, so
 * nothing here depends on render state, and a fresh array each render would only ever hand an
 * unstable identity to the category lookups below. This keeps it stable without relying on the
 * React Compiler to memoize it.
 */
const SearchCategories: Category[] = AllCategories.concat(MarketplaceCategories, PostCategories);

const CategoryDropdown = styled(Button)(({ theme }) => ({
  backgroundColor: "#E19941",
  borderBottomRightRadius: 0,
  borderTopRightRadius: 0,
  color: theme.palette.common.white,
  height: "56px",
  fontSize: "0.9em",
  minWidth: "12rem",
  "&:hover": {
    backgroundColor: "#E19941",
  },
}));
const CategoryDropdownSmall = styled(Button)(({ theme }) => ({
  backgroundColor: "#E19941",
  color: theme.palette.common.white,
  height: "56px",
  marginRight: "1rem",
  "&:hover": {
    backgroundColor: "#E19941",
  },
  padding: 0,
}));
const SearchBox = styled(TextField)(({ theme }) => ({
  boxSizing: "border-box",
  width: "100%",
  "& label.Mui-focused": {
    color: theme.palette.common.black,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#E19941",
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
    "&:hover fieldset": {
      borderColor: "#E19941",
      borderWidth: "1px",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E19941",
      borderWidth: "1px",
      boxShadow: "inset -1px 0px 0px 1px #E19941",
      outline: 0,
    },
  },
}));
const CategoryMenu = styled(Menu)(() => ({
  zIndex: 10,
}));
const SearchActions = styled(InputAdornment)(({ theme }) => ({
  gap: theme.spacing(0.25),
}));
const AskAiButton = styled(Button)(({ theme }) => ({
  minWidth: "auto",
  padding: theme.spacing(0.5, 1),
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    minWidth: theme.spacing(4),
    padding: theme.spacing(0.5),
    "& .MuiButton-startIcon": {
      margin: 0,
    },
    "& .AskAiButton-label": {
      display: "none",
    },
  },
}));

type SearchBarProps = {
  embedded?: boolean;
  searchParams?: URLSearchParams;
  searchTerm: string;
  aiModeAvailable?: boolean;
  onCategoryChanged: (category: Category) => void;
  onSearch: (searchTerm: string) => void;
  onAiSearch?: (searchTerm: string) => void;
  onSearchCleared: (searchTerm: string) => void;
};

export default function SearchBar(props: Readonly<SearchBarProps>) {
  const { embedded, searchParams, onCategoryChanged, onSearch, onAiSearch, onSearchCleared } = props;

  const anchorRef = useRef<HTMLButtonElement>(null);

  const queryParam = searchParams?.get("query") ?? null;
  const categoryParam = searchParams?.get("category") ?? null;

  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [categoryMenuAnchorEl, setCategoryMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>(() => queryParam ?? props.searchTerm);
  const [selectedCategory, setSelectedCategory] = useState(
    () => SearchCategories.find((category) => category.id === categoryParam) ?? SearchCategories[0],
  );
  const canUseAiSearch = (props.aiModeAvailable ?? true) && selectedCategory?.id === "all";

  // The box is the user's until something outside it actually changes. Each of these tracks the last
  // value it acted on, so a re-render on its own never overwrites half-typed text - re-reading the
  // query string on every render is what used to wipe it a moment after each keystroke.
  const [prevSearchTermProp, setPrevSearchTermProp] = useState<string>(() => props.searchTerm);
  const [prevQueryParam, setPrevQueryParam] = useState<string | null>(() => queryParam);
  const [prevCategoryParam, setPrevCategoryParam] = useState<string | null>(() => categoryParam);

  if (props.searchTerm !== prevSearchTermProp) {
    setPrevSearchTermProp(props.searchTerm);
    setSearchTerm(props.searchTerm);
  }

  if (queryParam !== prevQueryParam) {
    setPrevQueryParam(queryParam);
    setSearchTerm(queryParam ?? "");
  }

  if (categoryParam !== prevCategoryParam) {
    setPrevCategoryParam(categoryParam);
    const categoryFromParams = SearchCategories.find((category) => category.id === categoryParam);
    setSelectedCategory(categoryFromParams ?? SearchCategories[0]);
  }

  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  function handleMenuClose(event: React.MouseEvent) {
    if (categoryMenuAnchorEl?.contains(event.target as Node)) return;
    setCategoryMenuOpen(false);
    setCategoryMenuAnchorEl(null);
  }

  function handleCategorySelected(event: React.MouseEvent, id: string) {
    handleMenuClose(event);
    const selectedCategory = SearchCategories.find((category) => category.id === id);
    if (!selectedCategory) return;
    setSelectedCategory(selectedCategory);
    setSearchTerm("");
    onCategoryChanged(selectedCategory);
  }

  return (
    <Grid container direction="row" sx= {{ justifyContent: "space-between", alignItems: "center" }}>
      <Grid>
        {!embedded && (
          <>
            {!isMediumDisplaySize && (
              <CategoryDropdown
                ref={anchorRef}
                disableElevation
                variant="contained"
                endIcon={<ArrowDropDownIcon />}
                onClick={(event) => {
                  setCategoryMenuOpen(true);
                  setCategoryMenuAnchorEl(event.currentTarget);
                }}
              >
                {selectedCategory ? selectedCategory.name : "All Categories"}
              </CategoryDropdown>
            )}
            {isMediumDisplaySize && (
              <CategoryDropdownSmall
                aria-label="Choose marketplace category"
                ref={anchorRef}
                disableElevation
                variant="contained"
                onClick={(event) => {
                  setCategoryMenuOpen(true);
                  setCategoryMenuAnchorEl(event.currentTarget);
                }}
              >
                <AppsIcon />
              </CategoryDropdownSmall>
            )}
          </>
        )}
      </Grid>
      <Grid size="grow">
        <SearchBox
          variant="outlined"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onKeyDown={(event) => {
            if ((event.target as HTMLElement).tagName !== "INPUT") return;
            if (event.key === KeyboardKey.Enter && searchTerm.trim() !== "") onSearch(searchTerm);
            if (event.key === KeyboardKey.Escape) setSearchTerm("");
          }}
          slotProps={{
            htmlInput: {
              "aria-label": "Search marketplace",
            },
            input: {
              placeholder: selectedCategory ? selectedCategory.description : "Search Entire Marketplace",
              sx: {
                backgroundColor: "#fff",
                "& :placeholder-shown": {
                  textOverflow: "ellipsis",
                },
              },
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    aria-label="Search marketplace"
                    onClick={() => onSearch(searchTerm)}
                    disabled={searchTerm.trim() === ""}
                    size="large"
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <SearchActions position="end">
                  {onAiSearch && canUseAiSearch && (
                    <AskAiButton
                      aria-label="Ask AI"
                      color="primary"
                      disabled={searchTerm.trim() === ""}
                      size="small"
                      startIcon={<AutoAwesomeIcon fontSize="small" />}
                      title="Use AI to interpret this search"
                      variant="text"
                      onClick={() => onAiSearch(searchTerm)}
                    >
                      <span className="AskAiButton-label">Ask AI</span>
                    </AskAiButton>
                  )}
                  <IconButton
                    aria-label="Clear search"
                    style={{ display: searchTerm === "" ? "none" : "flex" }}
                    onClick={() => {
                      setSearchTerm("");
                      onSearchCleared("");
                    }}
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                </SearchActions>
              ),
            },
          }}
        />
        <CategoryMenu
          keepMounted
          open={categoryMenuOpen}
          onClose={handleMenuClose}
          anchorEl={categoryMenuAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              style: {
                maxHeight: 48 * 10,
              },
            },
          }}
        >
          {SearchCategories.map((category) => (
            <MenuItem key={category.id} onClick={(event) => handleCategorySelected(event, category.id)}>
              {category.name}
            </MenuItem>
          ))}
        </CategoryMenu>
      </Grid>
    </Grid>
  );
}
